import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../../firebase";
import { colors, radius, spacing, typography, cardBase } from "../../utils/designTokens";
import { useToast } from "../../components/Toast";
import { useIsMobile } from "../../hooks/useResponsive";
import { useUser } from "../../context/UserContext";
import { useProspeccionData } from "../../hooks/useProspeccionData";
import { useProspeccionFilters } from "../../context/ProspeccionFilterContext";
import { STATUS_LABELS } from "../../utils/statusColors";
import { enrichPadronCallable } from "../../utils/habiApi";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import CrmFicha from "../../components/CrmFicha";
import AppSelect from "../../components/AppSelect";
import {
  FaBullseye, FaSearch, FaFileExcel, FaBuilding, FaTable, FaSyncAlt, FaLock, FaFlag,
} from "react-icons/fa";
import { estaAsignado, estaBloqueado, fmtFechaLiberacion } from "../../utils/crmAsignacion";
import { datosEdificio } from "../../utils/datosEdificio";
import { REPORTE_TIPO_LABELS } from "../../utils/prospeccionReportes";
import { toJSDate } from "../../utils/dates";

const CRM_STATES = ["sin_contactar", "contactado", "en_conversacion", "reunion", "propuesta", "ganado", "perdido", "descartado"];
const CRM_OPTS = [{ value: "", label: "CRM: cualquiera" }, ...CRM_STATES.map((e) => ({ value: e, label: STATUS_LABELS[e] || e }))];
const REGIMEN_OPTS = [
  { value: "", label: "Todos los regímenes" },
  { value: "ph_en_tramite", label: "PH en trámite" },
  { value: "ph_incorporado", label: "PH incorporado" },
  { value: "comun", label: "Común" },
];

function scoreColor(v) {
  const n = Number(v) || 0;
  if (n >= 80) return colors.success;
  if (n >= 60) return colors.primary;
  if (n >= 40) return colors.warning;
  return colors.gray600;
}
const fmt = (n) => (n == null || n === "" ? "—" : Number(n).toLocaleString("es-UY"));
const currentYear = new Date().getFullYear();

function obraCorta(p) {
  if (p.fechaAperturaObra) return `${String(p.fechaAperturaObra).slice(0, 4)} · permiso`;
  if (p.anioObraFallback) return `${p.anioObraFallback} · catastro`;
  return "—";
}
function anioObra(p) {
  if (p.fechaAperturaObra) return Number(String(p.fechaAperturaObra).slice(0, 4));
  return p.anioObraFallback ? Number(p.anioObraFallback) : null;
}

export default function Prospeccion({ setSelected }) {
  const { showToast } = useToast();
  const isMobile = useIsMobile();
  const { puntos, crmByPadron, comerciales, loading, reload, updateCrm } = useProspeccionData();
  const { filters, setFilter } = useProspeccionFilters();

  const [sortKey, setSortKey] = useState("score");
  const [sortDirection, setSortDirection] = useState("desc");
  const [vista, setVista] = useState("tabla"); // tabla | dev | reportes
  const [busq, setBusq] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [ficha, setFicha] = useState(null);

  const { userType } = useUser();
  const isSuperAdmin = Number(userType) === 2;

  // Reportes de duplicados/datos (solo superadmin). Colección chica: se lee
  // entera y se ordena en cliente para no requerir un índice compuesto.
  const [reportes, setReportes] = useState([]);
  const [reportesLoading, setReportesLoading] = useState(false);
  useEffect(() => {
    if (!isSuperAdmin) return;
    let alive = true;
    (async () => {
      setReportesLoading(true);
      try {
        const snap = await getDocs(collection(db, "prospeccion_reportes"));
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => (toJSDate(b.createdAt)?.getTime() || 0) - (toJSDate(a.createdAt)?.getTime() || 0));
        if (alive) setReportes(rows);
      } catch { /* sin rules publicadas o sin datos: la vista queda vacía */ }
      if (alive) setReportesLoading(false);
    })();
    return () => { alive = false; };
  }, [isSuperAdmin]);

  const reportesPendientes = useMemo(() => reportes.filter((r) => (r.estado || "pendiente") === "pendiente").length, [reportes]);

  async function resolverReporte(r, estado) {
    try {
      await updateDoc(doc(db, "prospeccion_reportes", r.id), {
        estado, resueltoPor: auth.currentUser?.email || "", resueltoAt: serverTimestamp(),
      });
      setReportes((rs) => rs.map((x) => (x.id === r.id ? { ...x, estado } : x)));
    } catch (e) {
      showToast(e.message || "No se pudo actualizar el reporte.", "error");
    }
  }

  function abrirDesdeReporte(r) {
    const local = puntos.find((p) => String(p.padron) === String(r.padron));
    setFicha(local || { padron: r.padron, direccion: r.direccion || null, barrio: r.barrio || null });
  }

  function parseDireccion(s) {
    const m = s.trim().match(/^(.*?)\s+(\d+)\s*$/);
    return m ? { calle: m[1], puerta: Number(m[2]) } : { calle: s.trim() };
  }

  // Buscador: por padrón o dirección → llega al prospecto y abre su ficha CRM.
  async function buscar() {
    const q = busq.trim();
    if (!q) return;
    setBuscando(true);
    try {
      if (/^\d+$/.test(q)) {
        const local = puntos.find((p) => String(p.padron) === q);
        if (local) { setFicha(local); setBuscando(false); return; }
      }
      const arg = /^\d+$/.test(q) ? { padron: Number(q) } : parseDireccion(q);
      const e = await enrichPadronCallable(arg);
      setFicha(detalleAPunto(e));
    } catch (e) {
      showToast(e.message || "No se pudo encontrar el prospecto.", "error");
    }
    setBuscando(false);
  }

  const barrios = useMemo(
    () => [...new Set(puntos.map((p) => p.barrio).filter(Boolean))].sort(),
    [puntos]
  );

  const filtrados = useMemo(() => puntos.filter((p) => {
    if (filters.barrio && (p.barrio || "") !== filters.barrio) return false;
    if (filters.estado && p.estadoPH !== filters.estado) return false;
    if (filters.vis && !p.vis) return false;
    if (Number(p.score || 0) < filters.minScore) return false;
    if (filters.crm && (crmByPadron[String(p.padron)]?.estado || "sin_contactar") !== filters.crm) return false;
    if (filters.sinAdmin && p.yaAdministrado) return false;
    if (filters.obra === "permiso" && !p.tienePermiso) return false;
    if (filters.obra === "sinpermiso" && p.tienePermiso) return false;
    if (filters.obra === "permiso5") {
      const a = anioObra(p);
      if (!p.tienePermiso || !a || a < currentYear - 5) return false;
    }
    return true;
  }), [puntos, filters, crmByPadron]);

  const sorted = useMemo(() => {
    const arr = [...filtrados];
    arr.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      const c = av > bv ? 1 : av < bv ? -1 : 0;
      return sortDirection === "asc" ? c : -c;
    });
    return arr;
  }, [filtrados, sortKey, sortDirection]);

  const devs = useMemo(() => {
    const m = new Map();
    for (const p of puntos) {
      if (!p.empresa) continue;
      const e = m.get(p.empresa) || { empresa: p.empresa, edificios: 0, scoreSum: 0 };
      e.edificios++; e.scoreSum += Number(p.score || 0);
      m.set(p.empresa, e);
    }
    return [...m.values()]
      .map((e) => ({ ...e, scoreProm: Math.round(e.scoreSum / e.edificios) }))
      .sort((a, b) => b.edificios - a.edificios);
  }, [puntos]);

  function onSort(k) {
    if (sortKey === k) setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDirection("desc"); }
  }

  async function exportar() {
    try {
      const XLSX = await import("xlsx");
      const rows = filtrados.map((p) => {
        const c = crmByPadron[String(p.padron)] || {};
        const ef = datosEdificio(p, c);
        return {
          Padron: p.padron, Direccion: ef.direccion || "", Barrio: p.barrio || "",
          Regimen: p.estadoPH, ViviendaPromovida: p.vis ? "Sí" : "No", Desarrollador: p.empresa || "",
          Score: p.score, Obra: obraCorta(p), m2: p.totalM2 || p.areaEdif, ValorCatastral: p.valorCatastral,
          Unidades: ef.nUnidades, AnioConstruccion: ef.anioConstruccion,
          DatosCorregidos: ef.hayCorrecciones ? "Sí" : "",
          YaAdministrado: p.yaAdministrado ? "Sí" : "No",
          CRM_Estado: c.estado || "sin_contactar", CRM_Asignado: c.asignadoA || "", CRM_ProximaAccion: c.proximaAccion || "",
        };
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Prospectos");
      XLSX.writeFile(wb, "prospeccion.xlsx");
    } catch (e) {
      showToast("No se pudo exportar.", "error");
    }
  }

  const crmBadge = (row) => {
    const c = crmByPadron[String(row.padron)];
    if (!c?.estado && !estaAsignado(c)) return <span style={{ color: colors.gray400 }}>—</span>;
    return (
      <div style={{ display: "grid", gap: 2, justifyItems: "start" }}>
        {c?.estado && <StatusBadge status={c.estado} size="sm" />}
        {estaAsignado(c) && <AsignadoChip crm={c} />}
      </div>
    );
  };

  const columns = [
    { key: "padron", label: "Padrón", sortable: true },
    { key: "direccion", label: "Dirección", sortable: true, render: (_v, row) => datosEdificio(row, crmByPadron[String(row.padron)]).direccion || "—" },
    { key: "barrio", label: "Barrio", sortable: true, render: (v) => v || "—" },
    { key: "estadoPH", label: "Régimen", render: (v) => <StatusBadge status={v} size="sm" /> },
    { key: "vis", label: "VP", render: (v) => (v ? <StatusBadge status="vivienda_promovida" label="VP" size="sm" /> : null) },
    { key: "fechaAperturaObra", label: "Obra", render: (_v, row) => obraCorta(row) },
    { key: "nUnidades", label: "Unid.", align: "right", sortable: true, render: (_v, row) => fmt(datosEdificio(row, crmByPadron[String(row.padron)]).nUnidades) },
    { key: "totalM2", label: "m²", align: "right", sortable: true, render: (v, row) => fmt(v || row.areaEdif) },
    { key: "score", label: "Score", align: "right", sortable: true, render: (v) => <span style={{ color: scoreColor(v) }}>{v}</span> },
    { key: "crm", label: "CRM", render: (_v, row) => crmBadge(row) },
  ];

  const devColumns = [
    { key: "empresa", label: "Desarrollador", sortable: true },
    { key: "edificios", label: "Edificios", align: "right", sortable: true },
    { key: "scoreProm", label: "Score prom.", align: "right", sortable: true, render: (v) => <span style={{ color: scoreColor(v) }}>{v}</span> },
  ];

  const reporteColumns = [
    { key: "createdAt", label: "Fecha", render: (v) => { const d = toJSDate(v); return d ? d.toLocaleDateString("es-UY") : "—"; } },
    { key: "padron", label: "Padrón", render: (v, r) => (
      <button type="button" onClick={() => abrirDesdeReporte(r)} style={linkBtn}>{v}</button>
    ) },
    { key: "direccion", label: "Dirección", render: (v) => v || "—" },
    { key: "tipo", label: "Tipo", render: (v) => REPORTE_TIPO_LABELS[v] || v || "—" },
    { key: "detalle", label: "Detalle", render: (v) => v || "—" },
    { key: "reportadoPor", label: "Reportado por", render: (v, r) => v || r.reportadoEmail || "—" },
    { key: "estado", label: "Estado", render: (v) => <StatusBadge status={v || "pendiente"} size="sm" /> },
    { key: "acciones", label: "Acciones", render: (_v, r) => ((r.estado || "pendiente") === "pendiente" ? (
      <div style={{ display: "flex", gap: spacing[1] }}>
        <button type="button" onClick={() => resolverReporte(r, "resuelta")} style={miniOkBtn}>Resuelta</button>
        <button type="button" onClick={() => resolverReporte(r, "descartado")} style={miniGhostBtn}>Descartar</button>
      </div>
    ) : null) },
  ];

  const tabBtn = (id, icon, label) => (
    <button
      onClick={() => setVista(id)}
      style={{
        display: "flex", alignItems: "center", gap: spacing[1],
        padding: `${spacing[2]}px ${spacing[3]}px`,
        background: vista === id ? colors.primary : colors.white,
        color: vista === id ? "#fff" : colors.gray700,
        border: `1px solid ${vista === id ? colors.primary : colors.gray200}`,
        borderRadius: radius.sm, cursor: "pointer", fontSize: typography.sizes.sm,
      }}
      aria-pressed={vista === id}
    >
      {icon} {label}
    </button>
  );

  return (
    <div style={{ padding: isMobile ? spacing[3] : spacing[6], maxWidth: 1280, margin: "0 auto" }}>
      {/* Buscador → ficha CRM */}
      <div style={{ ...cardBase, padding: isMobile ? spacing[3] : spacing[4], marginBottom: isMobile ? spacing[3] : spacing[5] }}>
        <div style={{ display: "flex", gap: spacing[2], alignItems: "center", flexWrap: "wrap" }}>
          <FaSearch color={colors.gray400} />
          <input
            style={{ flex: 1, minWidth: 220, padding: `${spacing[2]}px ${spacing[3]}px`, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, fontSize: typography.sizes.base, fontFamily: typography.fontFamily }}
            placeholder="Buscar por padrón (ej. 431482) o dirección (ej. LIBERTAD 2412)"
            value={busq}
            onChange={(e) => setBusq(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && buscar()}
            aria-label="Buscar padrón o dirección"
          />
          <button
            onClick={buscar}
            disabled={buscando}
            style={{ display: "flex", alignItems: "center", gap: spacing[1], padding: `${spacing[2]}px ${spacing[4]}px`, background: colors.primary, color: "#fff", border: "none", borderRadius: radius.sm, cursor: "pointer", fontSize: typography.sizes.base }}
          >
            <FaSearch size={12} /> {buscando ? "Buscando…" : "Buscar"}
          </button>
        </div>
      </div>

      {/* Toggle + acciones (solo desktop) */}
      {!isMobile && (
        <div style={{ display: "flex", gap: spacing[2], marginBottom: spacing[4], flexWrap: "wrap" }}>
          {tabBtn("tabla", <FaTable size={12} />, "Listado")}
          {tabBtn("dev", <FaBuilding size={12} />, "Por desarrollador")}
          {isSuperAdmin && tabBtn("reportes", <FaFlag size={12} />, reportesPendientes ? `Reportes (${reportesPendientes})` : "Reportes")}
          <div style={{ flex: 1 }} />
          <button onClick={reload} style={ghostBtn} aria-label="Refrescar"><FaSyncAlt size={13} /> Refrescar</button>
          <button onClick={exportar} style={ghostBtn} aria-label="Exportar Excel"><FaFileExcel size={13} /> Excel</button>
        </div>
      )}

      {/* Filtros (compartidos con el mapa) — mobile: grid 2 col proporcionado */}
      {vista === "tabla" && (isMobile ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[2], marginBottom: spacing[3] }}>
          <AppSelect value={filters.barrio} onChange={(v) => setFilter("barrio", v)} ariaLabel="Barrio"
            options={[{ value: "", label: "Todos los barrios" }, ...barrios.map((b) => ({ value: b, label: b }))]} />
          <AppSelect value={filters.crm} onChange={(v) => setFilter("crm", v)} options={CRM_OPTS} ariaLabel="Estado CRM" />
          <div style={{ ...mScoreL, gridColumn: "1 / -1" }}>
            <span style={{ fontSize: typography.sizes.sm, color: colors.gray600, whiteSpace: "nowrap" }}>Score ≥</span>
            <input type="number" min={0} max={100} value={filters.minScore} onChange={(e) => setFilter("minScore", Number(e.target.value) || 0)} style={mScoreInputL} aria-label="Score mínimo" />
          </div>
          <div aria-live="polite" style={{ gridColumn: "1 / -1", display: "flex", alignItems: "baseline", justifyContent: "center", gap: spacing[1], padding: `${spacing[1]}px ${spacing[3]}px`, background: colors.gray50, border: `1px solid ${colors.gray200}`, borderRadius: radius.full }}>
            <span style={{ fontSize: typography.sizes["2xl"], color: colors.primary }}>{fmt(filtrados.length)}</span>
            <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>
              {filters.crm ? (STATUS_LABELS[filters.crm] || "resultados").toLowerCase() : "resultados"}
            </span>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: spacing[3], alignItems: "center", flexWrap: "wrap", marginBottom: spacing[4] }}>
          <div style={{ width: 190 }}>
            <AppSelect value={filters.barrio} onChange={(v) => setFilter("barrio", v)} ariaLabel="Filtrar por barrio"
              options={[{ value: "", label: "Todos los barrios" }, ...barrios.map((b) => ({ value: b, label: b }))]} />
          </div>
          <div style={{ width: 190 }}>
            <AppSelect value={filters.estado} onChange={(v) => setFilter("estado", v)} options={REGIMEN_OPTS} ariaLabel="Filtrar por régimen" />
          </div>
          <div style={{ width: 190 }}>
            <AppSelect value={filters.crm} onChange={(v) => setFilter("crm", v)} options={CRM_OPTS} ariaLabel="Filtrar por estado CRM" />
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: spacing[1], fontSize: typography.sizes.base, color: colors.gray700 }}>
            <input type="checkbox" checked={filters.vis} onChange={(e) => setFilter("vis", e.target.checked)} /> Solo Vivienda Promovida
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: spacing[1], fontSize: typography.sizes.base, color: colors.gray700 }}>
            Score ≥
            <input type="number" min={0} max={100} value={filters.minScore} onChange={(e) => setFilter("minScore", Number(e.target.value) || 0)} style={{ width: 64, padding: spacing[1], border: `1px solid ${colors.gray200}`, borderRadius: radius.sm }} />
          </label>
          <div style={{ flex: 1 }} />
          <div aria-live="polite" title="Resultados que cumplen los filtros actuales"
            style={{ display: "flex", alignItems: "baseline", gap: spacing[1], padding: `${spacing[1]}px ${spacing[3]}px`, background: colors.gray50, border: `1px solid ${colors.gray200}`, borderRadius: radius.full, whiteSpace: "nowrap" }}>
            <span style={{ fontSize: typography.sizes["2xl"], color: colors.primary }}>{fmt(filtrados.length)}</span>
            <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>
              {filters.crm ? (STATUS_LABELS[filters.crm] || "resultados").toLowerCase() : "resultados"}
            </span>
          </div>
        </div>
      ))}

      {vista === "reportes" ? (
        reportesLoading ? (
          <div style={{ textAlign: "center", padding: spacing[8], color: colors.gray600 }}>Cargando reportes…</div>
        ) : reportes.length === 0 ? (
          <EmptyState
            icon={FaFlag}
            title="Sin reportes"
            description="Cuando un comercial marque un padrón como duplicado, aparece acá para revisarlo."
          />
        ) : (
          <DataTable columns={reporteColumns} data={reportes} />
        )
      ) : loading ? (
        <div style={{ textAlign: "center", padding: spacing[8], color: colors.gray600 }}>Cargando prospección…</div>
      ) : vista === "dev" ? (
        <DataTable
          columns={devColumns}
          data={devs}
          emptyIcon={FaBuilding}
          emptyTitle="Sin desarrolladores"
          emptyDescription="Todavía no hay prospectos de Vivienda Promovida con desarrollador."
        />
      ) : sorted.length === 0 ? (
        <EmptyState
          icon={FaBullseye}
          title="Sin prospectos"
          description="Los filtros no arrojan resultados, o el mapa aún se está poblando. También podés buscar un padrón/dirección arriba."
        />
      ) : isMobile ? (
        // Mobile: tarjetas compactas con lo importante (no tabla ancha con scroll).
        <div style={{ display: "grid", gap: spacing[2] }}>
          {sorted.slice(0, 300).map((p) => {
            const c = crmByPadron[String(p.padron)];
            const ef = datosEdificio(p, c);
            return (
              <div
                key={p.padron}
                onClick={() => setFicha(p)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setFicha(p)}
                style={{ ...cardBase, padding: spacing[3], cursor: "pointer" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: typography.sizes.base, color: colors.gray900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {ef.direccion || `Padrón ${p.padron}`}
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 3, color: scoreColor(p.score), fontSize: typography.sizes.lg }}>★ {p.score}</span>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 6 }}>
                  <StatusBadge status={p.estadoPH} size="sm" />
                  {p.vis && <StatusBadge status="vivienda_promovida" label="VP" size="sm" />}
                  {c?.estado && <StatusBadge status={c.estado} size="sm" />}
                  {estaAsignado(c) && <AsignadoChip crm={c} />}
                </div>
                <div style={{ marginTop: 6, fontSize: typography.sizes.sm, color: colors.gray600, display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
                  <span>Padrón {p.padron}</span>
                  <span>· {fmt(ef.nUnidades)} unid.</span>
                  <span>· {obraCorta(p)}</span>
                  {p.barrio && <span>· {p.barrio}</span>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={sorted}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={onSort}
          onRowClick={(row) => setFicha(row)}
        />
      )}

      <div style={{ marginTop: spacing[4], fontSize: typography.sizes.xs, color: colors.gray400 }}>
        {fmt(filtrados.length)} de {fmt(puntos.length)} · Fuente: Intendencia de Montevideo (Datos Abiertos) · Vivienda Promovida (ANV). Tocá una fila para abrir su ficha CRM.
      </div>

      <CrmFicha
        open={!!ficha}
        onClose={() => setFicha(null)}
        padron={ficha?.padron}
        punto={ficha}
        crm={ficha ? crmByPadron[String(ficha.padron)] : null}
        comerciales={comerciales}
        onSaved={updateCrm}
        setSelected={setSelected}
      />
    </div>
  );
}

// Adapta el objeto enriquecido (del buscador) al shape del punto para la ficha.
function detalleAPunto(e) {
  const permisos = e.permisos || [];
  const fechas = permisos.map((p) => p.fechaAprob).filter(Boolean).sort();
  const fObra = fechas.length ? fechas[fechas.length - 1] : null;
  return {
    padron: e.padron,
    lat: e.lat, lng: e.lng,
    score: e.score,
    estadoPH: e.estadoPH,
    barrio: e.barrio,
    direccion: e.direcciones && e.direcciones[0] ? `${e.direcciones[0].calle} ${e.direcciones[0].puerta}` : null,
    vis: !!(e.viviendaPromovida && e.viviendaPromovida.vis),
    empresa: e.viviendaPromovida && e.viviendaPromovida.obra ? e.viviendaPromovida.obra.empresa : null,
    valorCatastral: e.catastro ? e.catastro.totalValor : null,
    totalM2: e.catastro ? e.catastro.totalM2 : null,
    nUnidades: e.catastro ? e.catastro.nUnidades : null,
    anioConstruccion: e.catastro ? e.catastro.anioConstruccion : null,
    anioUltimaObra: e.catastro ? e.catastro.anioUltimaObra : null,
    areaEdif: Math.max(0, ...permisos.map((p) => Number(p.areaEdif) || 0)) || null,
    fechaAperturaObra: fObra,
    anioObraFallback: fObra ? null : (e.catastro ? e.catastro.anioUltimaObra : null),
    tienePermiso: !!fObra,
    yaAdministrado: false,
  };
}

// Chip "tomado por X": visible para todos en el listado. Gris oscuro si el lock
// de 60 días está vigente (tooltip con la fecha de liberación); apagado si venció.
function AsignadoChip({ crm }) {
  const vigente = estaBloqueado(crm);
  return (
    <span
      title={vigente ? `Bloqueado hasta ${fmtFechaLiberacion(crm) || "—"}` : "Venció el bloqueo: libre para tomar"}
      style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: typography.sizes.xs, color: vigente ? colors.gray700 : colors.gray400, whiteSpace: "nowrap" }}
    >
      <FaLock size={9} /> {crm.asignadoA || crm.asignadoEmail}{vigente ? "" : " (libre)"}
    </span>
  );
}

// Controles mobile del listado: proporcionados (misma altura, full-width en grid).
const mScoreL = { display: "flex", alignItems: "center", gap: 6, height: 42, padding: "0 10px", border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, background: "#fff", boxSizing: "border-box" };
const mScoreInputL = { flex: 1, minWidth: 0, width: "100%", border: "none", outline: "none", background: "transparent", fontSize: typography.sizes.base, fontFamily: typography.fontFamily, color: colors.gray900 };
const ghostBtn = {
  display: "flex", alignItems: "center", gap: spacing[1],
  padding: `${spacing[2]}px ${spacing[3]}px`,
  background: "#fff", border: `1px solid ${colors.gray200}`, borderRadius: radius.sm,
  cursor: "pointer", fontSize: typography.sizes.sm, color: colors.gray700,
};
// Vista de reportes (superadmin)
const linkBtn = { padding: 0, border: "none", background: "transparent", color: colors.primary, cursor: "pointer", fontSize: typography.sizes.md, fontFamily: "inherit", textDecoration: "underline" };
const miniOkBtn = { padding: "4px 10px", border: "none", borderRadius: radius.sm, background: colors.successLight, color: colors.successDark, cursor: "pointer", fontSize: typography.sizes.sm, fontFamily: "inherit", transition: "all 0.15s ease" };
const miniGhostBtn = { padding: "4px 10px", border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, background: colors.white, color: colors.gray700, cursor: "pointer", fontSize: typography.sizes.sm, fontFamily: "inherit", transition: "all 0.15s ease" };
